import React, { useEffect, useState, useCallback } from "react";
import { Box, Loader2, ChevronDown } from "lucide-react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/pages/Account";
import { toast } from "sonner";

const ORDER_STATUSES = ["pending", "confirmed", "processing", "shipped", "completed", "cancelled"];
const QUOTE_STATUSES = ["requested", "quoted", "accepted", "declined"];

const OrderRow = ({ order, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(order.status);
  const [total, setTotal] = useState(order.total ?? "");
  const [adminNotes, setAdminNotes] = useState(order.admin_notes || "");
  const [saving, setSaving] = useState(false);
  const statuses = order.type === "quote" ? QUOTE_STATUSES : ORDER_STATUSES;

  const save = async () => {
    setSaving(true);
    try {
      const body = { status, admin_notes: adminNotes };
      if (total !== "" && total !== null) body.total = parseFloat(total);
      const { data } = await client.patch(`/admin/orders/${order.id}`, body);
      toast.success("Order updated");
      onUpdated(data);
      setOpen(false);
    } catch { toast.error("Failed to update"); } finally { setSaving(false); }
  };

  return (
    <div data-testid={`admin-order-${order.order_no}`} className="bg-white border border-border rounded-lg overflow-hidden">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-4 p-4 text-left hover:bg-surface/40">
        <div className="flex items-center gap-3 min-w-0">
          <div><div className="mono text-sm font-semibold text-graphite">{order.order_no}</div><div className="text-xs text-steel-light truncate">{order.customer?.name} · {order.customer?.company || order.customer?.email}</div></div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-xs uppercase tracking-wider text-steel-light hidden sm:block">{order.type}</span>
          <span className="mono text-sm text-graphite hidden sm:block">{order.total != null ? `$${Number(order.total).toFixed(2)}` : "—"}</span>
          <StatusBadge status={order.status} />
          <ChevronDown className={`w-4 h-4 text-steel-light transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>

      {open && (
        <div className="border-t border-border p-4 bg-surface/30">
          <div className="grid lg:grid-cols-2 gap-5">
            <div>
              <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-2">Items</div>
              <div className="space-y-2">
                {order.items.map((it) => (
                  <div key={it.sku} className="flex items-center gap-2.5 text-sm">
                    <div className="w-9 h-9 rounded bg-white border border-border grid place-items-center p-1 shrink-0">{it.image ? <img src={it.image} alt="" className="max-h-full object-contain mix-blend-multiply" /> : <Box className="w-4 h-4 text-steel-light" />}</div>
                    <div className="min-w-0 flex-1"><span className="mono text-xs text-orange">{it.sku}</span><div className="text-graphite line-clamp-1">{it.name}</div></div>
                    <span className="mono text-steel">×{it.qty}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-steel-light">
                <div>Delivery: {order.address?.line1 || "—"}, {order.address?.city} {order.address?.postal_code}</div>
                {order.notes && <div className="mt-1">Customer notes: {order.notes}</div>}
              </div>
            </div>

            <div>
              <div className="mono text-xs uppercase tracking-[0.15em] text-steel-light mb-2">Manage</div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-steel-light">Status</label>
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger data-testid={`order-status-${order.order_no}`} className="h-10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-steel-light">Quoted / Confirmed total (CAD)</label>
                  <Input data-testid={`order-total-${order.order_no}`} type="number" step="0.01" value={total} onChange={(e) => setTotal(e.target.value)} placeholder="e.g. 149.90" className="h-10 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-steel-light">Reply to customer</label>
                  <Input value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} placeholder="Note visible to customer" className="h-10 mt-1" />
                </div>
                <Button data-testid={`order-save-${order.order_no}`} onClick={save} disabled={saving} className="bg-orange hover:bg-orange-hover">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (type !== "all") params.type = type;
    if (status !== "all") params.status = status;
    client.get("/admin/orders", { params }).then((r) => setOrders(r.data.items)).finally(() => setLoading(false));
  }, [type, status]);

  useEffect(() => { load(); }, [load]);
  const onUpdated = (updated) => setOrders((os) => os.map((o) => (o.id === updated.id ? updated : o)));

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight text-graphite">Orders & Quotes</h1>
      <p className="text-sm text-steel-light mt-1 mb-5">{orders.length} records — confirm orders, set quoted prices and reply to customers.</p>

      <div className="flex gap-3 mb-5">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="filter-type" className="w-40 h-10"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All types</SelectItem><SelectItem value="order">Orders</SelectItem><SelectItem value="quote">Quotes</SelectItem></SelectContent>
        </Select>
      </div>

      {loading ? <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-steel-light" /></div>
        : orders.length === 0 ? <div className="border border-border rounded-lg py-16 text-center text-steel-light">No orders yet.</div>
        : <div className="space-y-2">{orders.map((o) => <OrderRow key={o.id} order={o} onUpdated={onUpdated} />)}</div>}
    </div>
  );
};

export default Orders;
